"use client"

import { FormikProvider, useFormik } from "formik"

import { FormInput, FormTextarea } from "@/components/form-inputs"
import { Button } from "@/components/ui/button"
import { DiffLines } from "@/components/ui/prompt-diff"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useAcceptKbSuggestionMutation,
  useRejectSuggestionMutation,
} from "@/hooks/mutations/conversation-quality"
import { usePermissions } from "@/hooks/use-permissions"
import { AI_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { articleDestination, articleLines, describeWaiting } from "@/lib/conversation-quality/change-display"
import type { QualitySuggestionsResult } from "@/server-actions/conversation-quality"

type ArticleSuggestion = Extract<
  QualitySuggestionsResult["suggestions"][number],
  { kind: "kb_addition" }
>

/** The server re-checks both permissions and that the draft is still waiting. */
export function ArticleReviewDialog({
  suggestion,
  open,
  onOpenChange,
}: {
  suggestion: ArticleSuggestion
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { can } = usePermissions()
  const canAddDocuments = can(AI_PERMISSIONS.MANAGE_KNOWLEDGE_BASE)
  const close = () => onOpenChange(false)
  const accept = useAcceptKbSuggestionMutation({ onAccepted: close })
  const reject = useRejectSuggestionMutation("kb_addition", { onRejected: close })
  const isDeciding = accept.isPending || reject.isPending
  const { decline } = describeWaiting({
    ...suggestion,
    operation: null,
    edit: null,
    checkReason: null,
    replacing: suggestion.before?.author ?? null,
  })

  const formik = useFormik<ArticleValues>({
    initialValues: { title: suggestion.title, content: suggestion.content },
    onSubmit: async (values, helpers) => {
      try {
        await accept.mutateAsync({
          input: { suggestionId: suggestion.id, ...values },
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling has shown the toast and placed any field errors already.
      }
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Review article</DialogTitle>
            <DialogDescription>
              Drafted for {suggestion.agentName} from what your team told customers asking about
              “{suggestion.topicLabel}”. Edit it before adding it, or decline it.
            </DialogDescription>
          </DialogHeader>

          <FormikProvider value={formik}>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground break-words">
                {articleDestination(suggestion.agentName, suggestion.topicCategory, suggestion.before)}
              </p>
              <FormInput name="title" label="Title" />
              <FormTextarea name="content" label="Article" rows={12} />
              {suggestion.before && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">What changes</p>
                  <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/30 p-2 font-mono text-xs">
                    <DiffLines lines={articleLines(suggestion.before, formik.values)} />
                  </div>
                </div>
              )}
              {!canAddDocuments && (
                <p className="text-xs text-muted-foreground">
                  Adding it needs permission to manage the knowledge base.
                </p>
              )}
            </div>
          </FormikProvider>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => reject.mutate({ input: { suggestionId: suggestion.id } })}
              disabled={isDeciding}
            >
              {decline}
            </Button>
            <Button type="submit" disabled={isDeciding || !canAddDocuments}>
              Add to knowledge base
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ArticleValues {
  title: string;
  content: string;
}
